import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  AreaChart,
  Area,
  BarChart,
  Bar,
} from 'recharts';
import {
  Plus,
  LayoutTemplate,
  Save,
  Copy,
  ExternalLink,
  Sparkles,
  Palette,
  MoveRight,
  PanelsTopLeft,
  ChartColumn,
  Globe,
  GripVertical,
  Trash2,
  BarChart3,
  ArrowLeft,
  Share2,
  Edit3,
  MousePointerSquareDashed,
  Type,
  Mail,
  PhoneCall,
  TextCursorInput,
  ListFilter,
  CircleDot,
  CheckSquare,
  Settings2,
  MonitorSmartphone,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PALETTE = [
  { type: 'heading', label: 'Heading', description: 'Strong section title', icon: Type },
  { type: 'paragraph', label: 'Paragraph', description: 'Supporting copy', icon: TextCursorInput },
  { type: 'text', label: 'Text input', description: 'Generic single-line field', icon: Type },
  { type: 'email', label: 'Email input', description: 'Email capture field', icon: Mail },
  { type: 'phone', label: 'Phone input', description: 'Telephone field', icon: PhoneCall },
  { type: 'textarea', label: 'Textarea', description: 'Long form message field', icon: TextCursorInput },
  { type: 'select', label: 'Select', description: 'Dropdown choice', icon: ListFilter },
  { type: 'radio_group', label: 'Radio group', description: 'Single choice list', icon: CircleDot },
  { type: 'checkbox_group', label: 'Checkbox group', description: 'Multiple choice list', icon: CheckSquare },
  { type: 'checkbox', label: 'Checkbox', description: 'Consent or agreement', icon: CheckSquare },
];

const generateId = (prefix) => `${prefix}${Math.random().toString(16).slice(2, 10)}`;
const DEFAULT_DISQUALIFICATION_MESSAGE = 'Thanks for your interest. Based on your answers, this form is not the right fit.';

const slugify = (value, fallback = 'field') => {
  const raw = (value || fallback).toLowerCase().trim();
  return raw.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || fallback;
};

const createOption = (label = 'Option') => ({
  option_id: generateId('opt_'),
  label,
  value: slugify(label, 'option'),
  disqualify: false,
});

const createMetaPixelTemplate = () => ({
  enabled: false,
  pixel_id: '',
  track_view: true,
  track_start: true,
  track_submit: true,
});

const normalizeComponent = (component) => ({
  disqualify: false,
  disqualification_message: '',
  ...component,
  options: (component.options || []).map((option) => ({
    disqualify: false,
    ...option,
  })),
});

const hydrateForm = (form) => ({
  ...form,
  disqualification_message: form.disqualification_message || DEFAULT_DISQUALIFICATION_MESSAGE,
  meta_pixel: {
    ...createMetaPixelTemplate(),
    ...(form.meta_pixel || {}),
  },
  steps: (form.steps || []).map((step) => ({
    ...step,
    components: (step.components || []).map(normalizeComponent),
  })),
});

const createComponentTemplate = (type) => {
  const base = {
    component_id: generateId('cmp_'),
    type,
    label: '',
    text: '',
    placeholder: '',
    required: false,
    enabled: true,
    field_key: '',
    lead_field: 'custom',
    disqualify: false,
    disqualification_message: '',
    options: [],
  };

  switch (type) {
    case 'heading':
      return { ...base, text: 'Tell us what you need' };
    case 'paragraph':
      return { ...base, text: 'Add supporting context for this step.' };
    case 'email':
      return { ...base, label: 'Email', placeholder: 'you@example.com', required: true, field_key: 'email', lead_field: 'email' };
    case 'phone':
      return { ...base, label: 'Phone', placeholder: '+44 7700 900123', field_key: 'phone', lead_field: 'phone' };
    case 'textarea':
      return { ...base, label: 'Message', placeholder: 'Tell us more', field_key: 'message', lead_field: 'message' };
    case 'select':
      return { ...base, label: 'Select an option', field_key: generateId('field_'), options: [createOption('Option 1'), createOption('Option 2')] };
    case 'radio_group':
      return { ...base, label: 'Choose one', field_key: generateId('field_'), options: [createOption('Option 1'), createOption('Option 2')] };
    case 'checkbox_group':
      return { ...base, label: 'Choose all that apply', field_key: generateId('field_'), options: [createOption('Option 1'), createOption('Option 2')] };
    case 'checkbox':
      return { ...base, label: 'I agree to be contacted', field_key: generateId('field_') };
    case 'text':
    default:
      return { ...base, label: 'Full name', placeholder: 'Your full name', required: true, field_key: 'full_name', lead_field: 'name' };
  }
};

const createStepTemplate = (index, formName = 'New form') => ({
  step_id: generateId('step_'),
  title: index === 0 ? 'Your details' : `Step ${index + 1}`,
  description: index === 0 ? 'Collect the core information first.' : 'Add more context before the final submission.',
  next_button_text: 'Continue',
  back_button_text: 'Back',
  submit_button_text: 'Submit',
  components: index === 0 ? [
    { ...createComponentTemplate('heading'), text: formName },
    { ...createComponentTemplate('paragraph'), text: 'Use this form to capture and qualify enquiries.' },
    createComponentTemplate('text'),
    createComponentTemplate('email'),
  ] : [
    createComponentTemplate('phone'),
    createComponentTemplate('textarea'),
  ],
});

const clone = (value) => JSON.parse(JSON.stringify(value));

const FormSectionNav = ({ formId, isShareRoute, isBuilderRoute, navigate }) => (
  <div className="inline-flex rounded-full border border-slate-200 bg-white p-1 shadow-sm">
    <button
      type="button"
      onClick={() => navigate(`/forms/${formId}`)}
      className={`rounded-full px-4 py-2 text-sm font-medium transition ${isBuilderRoute ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
    >
      Builder
    </button>
    <button
      type="button"
      onClick={() => navigate(`/forms/${formId}/share`)}
      className={`rounded-full px-4 py-2 text-sm font-medium transition ${isShareRoute ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
    >
      Share
    </button>
    <button
      type="button"
      onClick={() => navigate('/forms/analytics')}
      className="rounded-full px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
    >
      Analytics
    </button>
  </div>
);

const renderComponentPreview = (component) => {
  if (!component.enabled) {
    return <div className="text-xs text-slate-400">Disabled</div>;
  }

  switch (component.type) {
    case 'heading':
      return <div className="text-lg font-bold text-slate-900">{component.text || 'Heading'}</div>;
    case 'paragraph':
      return <p className="text-sm leading-6 text-slate-500">{component.text || 'Paragraph copy'}</p>;
    case 'textarea':
      return (
        <div className="space-y-2">
          <div className="text-sm font-medium text-slate-700">{component.label || 'Textarea'}</div>
          <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-400">{component.placeholder || 'Long form input'}</div>
        </div>
      );
    case 'select':
      return (
        <div className="space-y-2">
          <div className="text-sm font-medium text-slate-700">{component.label || 'Select'}</div>
          <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-500">Select an option</div>
        </div>
      );
    case 'radio_group':
    case 'checkbox_group':
      return (
        <div className="space-y-2">
          <div className="text-sm font-medium text-slate-700">{component.label || 'Choices'}</div>
          <div className="grid gap-2">
            {component.options?.map((option) => (
              <div key={option.option_id} className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600">
                {option.label}
              </div>
            ))}
          </div>
        </div>
      );
    case 'checkbox':
      return <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-600">{component.label || 'Checkbox field'}</div>;
    default:
      return (
        <div className="space-y-2">
          <div className="text-sm font-medium text-slate-700">{component.label || 'Input'}</div>
          <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-400">{component.placeholder || 'Input field'}</div>
        </div>
      );
  }
};

const BuilderInputShell = ({ children, selected, accentColor }) => (
  <div
    className={`rounded-[22px] border bg-white transition ${
      selected ? 'shadow-[0_18px_40px_rgba(15,23,42,0.12)]' : 'shadow-sm'
    }`}
    style={{
      borderColor: selected ? accentColor : '#dbe4ee',
      boxShadow: selected ? `0 0 0 3px ${accentColor}22, 0 18px 40px rgba(15,23,42,0.12)` : undefined,
    }}
  >
    {children}
  </div>
);

const renderLiveFormComponent = ({ component, selected, accentColor, onSelect }) => {
  const label = component.label || component.text || component.type;
  const commonLabel = component.label ? (
    <label className="mb-2 block text-[13px] font-semibold text-slate-700">
      {component.label}
      {component.required ? <span className="ml-1 text-rose-500">*</span> : null}
    </label>
  ) : null;

  if (!component.enabled) {
    return null;
  }

  if (component.type === 'heading') {
    return (
      <button
        type="button"
        onClick={onSelect}
        className="w-full rounded-[24px] px-1 py-2 text-left transition"
      >
        <div
          className={`text-[30px] font-black tracking-tight ${selected ? 'opacity-100' : 'opacity-95'}`}
          style={{ color: '#0f172a' }}
        >
          {component.text || 'Heading'}
        </div>
      </button>
    );
  }

  if (component.type === 'paragraph') {
    return (
      <button type="button" onClick={onSelect} className="w-full rounded-[20px] px-1 py-1 text-left">
        <p className="max-w-xl text-[15px] leading-7 text-slate-500">{component.text || 'Paragraph copy'}</p>
      </button>
    );
  }

  if (component.type === 'textarea') {
    return (
      <button type="button" onClick={onSelect} className="block w-full text-left">
        <BuilderInputShell selected={selected} accentColor={accentColor}>
          <div className="p-5">
            {commonLabel}
            <div className="min-h-[120px] rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-400">
              {component.placeholder || 'Tell us more'}
            </div>
          </div>
        </BuilderInputShell>
      </button>
    );
  }

  if (component.type === 'select') {
    return (
      <button type="button" onClick={onSelect} className="block w-full text-left">
        <BuilderInputShell selected={selected} accentColor={accentColor}>
          <div className="p-5">
            {commonLabel}
            <div className="flex items-center justify-between rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-500">
              <span>{component.placeholder || 'Select an option'}</span>
              <span>▾</span>
            </div>
          </div>
        </BuilderInputShell>
      </button>
    );
  }

  if (component.type === 'radio_group' || component.type === 'checkbox_group') {
    return (
      <button type="button" onClick={onSelect} className="block w-full text-left">
        <BuilderInputShell selected={selected} accentColor={accentColor}>
          <div className="p-5">
            {commonLabel}
            <div className="grid gap-3">
              {(component.options || []).map((option) => (
                <div key={option.option_id} className="flex items-center gap-3 rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                  <div className={`h-4 w-4 rounded-full border ${component.type === 'radio_group' ? 'border-slate-400' : 'rounded-[5px]'} border-slate-400`} />
                  <span>{option.label}</span>
                </div>
              ))}
            </div>
          </div>
        </BuilderInputShell>
      </button>
    );
  }

  if (component.type === 'checkbox') {
    return (
      <button type="button" onClick={onSelect} className="block w-full text-left">
        <BuilderInputShell selected={selected} accentColor={accentColor}>
          <div className="flex items-center gap-3 p-5">
            <div className="h-5 w-5 rounded-[6px] border border-slate-400 bg-white" />
            <span className="text-sm text-slate-700">{label}</span>
          </div>
        </BuilderInputShell>
      </button>
    );
  }

  return (
    <button type="button" onClick={onSelect} className="block w-full text-left">
      <BuilderInputShell selected={selected} accentColor={accentColor}>
        <div className="p-5">
          {commonLabel}
          <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-400">
            {component.placeholder || 'Input value'}
          </div>
        </div>
      </BuilderInputShell>
    </button>
  );
};

const FormsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { formId } = useParams();
  const { user, selectedBusiness, selectedCampaign } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [campaigns, setCampaigns] = useState([]);
  const [forms, setForms] = useState([]);
  const [draftForm, setDraftForm] = useState(null);
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [selectedComponentId, setSelectedComponentId] = useState(null);
  const [overview, setOverview] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsSummary, setAnalyticsSummary] = useState(null);
  const [analyticsFilter, setAnalyticsFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 13);
    return date.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [dragItem, setDragItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createData, setCreateData] = useState({ name: '', campaign_id: '' });

  const businessId = selectedBusiness?.business_id || user?.business_id;
  const isAnalyticsRoute = location.pathname === '/forms/analytics';
  const isShareRoute = location.pathname.endsWith('/share');
  const isBuilderRoute = Boolean(formId) && !isShareRoute && !isAnalyticsRoute;

  const selectedStep = draftForm?.steps?.find((step) => step.step_id === selectedStepId) || draftForm?.steps?.[0] || null;
  const selectedComponent = selectedStep?.components?.find((component) => component.component_id === selectedComponentId) || null;
  const selectedAnalyticsForm = analyticsFilter !== 'all' ? forms.find((form) => form.form_id === analyticsFilter) : null;

  useEffect(() => {
    if (!businessId) return;
    fetchCampaigns();
    fetchForms();
    fetchOverview();
  }, [businessId]);

  useEffect(() => {
    if (campaigns.length > 0 && !createData.campaign_id) {
      setCreateData((prev) => ({ ...prev, campaign_id: selectedCampaign?.campaign_id || campaigns[0].campaign_id }));
    }
  }, [campaigns, createData.campaign_id, selectedCampaign?.campaign_id]);

  useEffect(() => {
    if (!forms.length) {
      setDraftForm(null);
      setAnalytics(null);
      return;
    }

    if (isBuilderRoute || isShareRoute) {
      const matched = forms.find((form) => form.form_id === formId);
      if (!matched) {
        navigate('/forms', { replace: true });
        return;
      }
      const hydrated = hydrateForm(matched);
      setDraftForm(clone(hydrated));
      setSelectedStepId(hydrated.steps?.[0]?.step_id || null);
      setSelectedComponentId(hydrated.steps?.[0]?.components?.[0]?.component_id || null);
      fetchAnalytics(hydrated.form_id);
    } else {
      setDraftForm(null);
    }
  }, [forms, formId, isBuilderRoute, isShareRoute, navigate]);

  useEffect(() => {
    if (!isAnalyticsRoute) return;
    if (analyticsFilter === 'all') {
      setAnalytics(null);
    } else {
      fetchAnalytics(analyticsFilter);
    }
    fetchAnalyticsSummary();
  }, [analyticsFilter, isAnalyticsRoute, dateFrom, dateTo]);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchCampaigns = async () => {
    const response = await fetch(`${API_URL}/api/campaigns?business_id=${businessId}`, {
      headers: authHeaders(),
      credentials: 'include',
    });
    if (response.ok) {
      setCampaigns(await response.json());
    }
  };

  const fetchForms = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/forms?business_id=${businessId}`, {
        headers: authHeaders(),
        credentials: 'include',
      });
      if (response.ok) {
        const data = (await response.json()).map(hydrateForm);
        setForms(data);
        if (!formId && !analyticsFilter) {
          setAnalyticsFilter('all');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchOverview = async () => {
    const params = new URLSearchParams({ business_id: businessId });
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    const response = await fetch(`${API_URL}/api/forms-summary?${params.toString()}`, {
      headers: authHeaders(),
      credentials: 'include',
    });
    if (response.ok) {
      setOverview(await response.json());
    }
  };

  const fetchAnalytics = async (targetFormId) => {
    const params = new URLSearchParams();
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    const response = await fetch(`${API_URL}/api/forms/${targetFormId}/analytics?${params.toString()}`, {
      headers: authHeaders(),
      credentials: 'include',
    });
    if (response.ok) {
      setAnalytics(await response.json());
    }
  };

  const fetchAnalyticsSummary = async () => {
    const params = new URLSearchParams({ business_id: businessId });
    if (analyticsFilter !== 'all') params.set('form_id', analyticsFilter);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    const response = await fetch(`${API_URL}/api/forms-analytics?${params.toString()}`, {
      headers: authHeaders(),
      credentials: 'include',
    });
    if (response.ok) {
      setAnalyticsSummary(await response.json());
    }
  };

  const updateDraft = (updater) => {
    setDraftForm((current) => {
      const next = typeof updater === 'function' ? updater(clone(current)) : updater;
      return next;
    });
  };

  const createForm = async () => {
    if (!createData.name || !createData.campaign_id) {
      toast.error('Provide a form name and campaign');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/forms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({
          name: createData.name,
          campaign_id: createData.campaign_id,
          steps: [createStepTemplate(0, createData.name), createStepTemplate(1, createData.name)],
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create form');
      }
      setShowCreateDialog(false);
      setCreateData((prev) => ({ ...prev, name: '' }));
      toast.success('Multi-step form created');
      await fetchForms();
      await fetchOverview();
      navigate(`/forms/${data.form_id}`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const saveForm = async () => {
    if (!draftForm) return;
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/forms/${draftForm.form_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        credentials: 'include',
        body: JSON.stringify({
          name: draftForm.name,
          campaign_id: draftForm.campaign_id,
          status: draftForm.status,
          success_message: draftForm.success_message,
          disqualification_message: draftForm.disqualification_message,
          allowed_domains: draftForm.allowed_domains,
          steps: draftForm.steps,
          theme: draftForm.theme,
          meta_pixel: draftForm.meta_pixel,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save form');
      }
      toast.success('Form saved');
      await fetchForms();
      await fetchOverview();
      const hydrated = hydrateForm(data);
      setDraftForm(clone(hydrated));
      setSelectedStepId(hydrated.steps?.[0]?.step_id || null);
      setSelectedComponentId(hydrated.steps?.[0]?.components?.[0]?.component_id || null);
      if (isAnalyticsRoute && analyticsFilter === hydrated.form_id) {
        fetchAnalytics(hydrated.form_id);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const addStep = () => {
    if (!draftForm) return;
    updateDraft((next) => {
      const step = createStepTemplate(next.steps.length, next.name);
      step.components = [{ ...createComponentTemplate('heading'), text: step.title }];
      next.steps.push(step);
      return next;
    });
  };

  const removeStep = (stepId) => {
    if (!draftForm || draftForm.steps.length <= 1) return;
    updateDraft((next) => {
      next.steps = next.steps.filter((step) => step.step_id !== stepId);
      return next;
    });
    const remaining = draftForm.steps.find((step) => step.step_id !== stepId);
    setSelectedStepId(remaining?.step_id || null);
    setSelectedComponentId(null);
  };

  const updateStepField = (stepId, key, value) => {
    updateDraft((next) => {
      const step = next.steps.find((entry) => entry.step_id === stepId);
      if (step) {
        step[key] = value;
      }
      return next;
    });
  };

  const addComponentToStep = (stepId, type, insertIndex = null) => {
    updateDraft((next) => {
      const step = next.steps.find((entry) => entry.step_id === stepId);
      if (!step) return next;
      const component = createComponentTemplate(type);
      if (insertIndex === null) {
        step.components.push(component);
      } else {
        step.components.splice(insertIndex, 0, component);
      }
      setSelectedComponentId(component.component_id);
      return next;
    });
  };

  const removeComponent = (componentId) => {
    if (!selectedStep) return;
    updateDraft((next) => {
      const step = next.steps.find((entry) => entry.step_id === selectedStep.step_id);
      if (!step) return next;
      step.components = step.components.filter((component) => component.component_id !== componentId);
      return next;
    });
    setSelectedComponentId(null);
  };

  const updateComponent = (componentId, key, value) => {
    updateDraft((next) => {
      for (const step of next.steps) {
        const component = step.components.find((entry) => entry.component_id === componentId);
        if (!component) continue;
        component[key] = value;
        if (key === 'label' && (!component.field_key || component.field_key.startsWith('field_'))) {
          component.field_key = slugify(value, component.field_key || 'field');
        }
      }
      return next;
    });
  };

  const addOptionToComponent = () => {
    if (!selectedComponent) return;
    updateDraft((next) => {
      for (const step of next.steps) {
        const component = step.components.find((entry) => entry.component_id === selectedComponent.component_id);
        if (!component) continue;
        component.options.push(createOption(`Option ${component.options.length + 1}`));
      }
      return next;
    });
  };

  const updateOption = (componentId, optionId, key, value) => {
    updateDraft((next) => {
      for (const step of next.steps) {
        const component = step.components.find((entry) => entry.component_id === componentId);
        if (!component) continue;
        const option = component.options.find((entry) => entry.option_id === optionId);
        if (!option) continue;
        option[key] = value;
        if (key === 'label') {
          option.value = slugify(value, option.value || 'option');
        }
      }
      return next;
    });
  };

  const removeOption = (componentId, optionId) => {
    updateDraft((next) => {
      for (const step of next.steps) {
        const component = step.components.find((entry) => entry.component_id === componentId);
        if (!component) continue;
        component.options = component.options.filter((option) => option.option_id !== optionId);
      }
      return next;
    });
  };

  const handleDropAtIndex = (stepId, targetIndex) => {
    if (!dragItem) return;
    updateDraft((next) => {
      const targetStep = next.steps.find((step) => step.step_id === stepId);
      if (!targetStep) return next;

      if (dragItem.source === 'palette') {
        targetStep.components.splice(targetIndex, 0, createComponentTemplate(dragItem.type));
        return next;
      }

      const fromStep = next.steps.find((step) => step.step_id === dragItem.stepId);
      if (!fromStep) return next;
      const fromIndex = fromStep.components.findIndex((component) => component.component_id === dragItem.componentId);
      if (fromIndex === -1) return next;
      const [moved] = fromStep.components.splice(fromIndex, 1);
      const adjustedIndex = dragItem.stepId === stepId && fromIndex < targetIndex ? targetIndex - 1 : targetIndex;
      targetStep.components.splice(adjustedIndex, 0, moved);
      return next;
    });
    setDragItem(null);
    setDragOverIndex(null);
  };

  const beginCanvasDrag = (stepId, componentId) => {
    setDragItem({ source: 'canvas', stepId, componentId });
  };

  const endCanvasDrag = () => {
    setDragItem(null);
    setDragOverIndex(null);
  };

  const copyToClipboard = async (value, label) => {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };

  const renderOverviewCards = () => overview && (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
      {[
        { label: 'Forms', value: overview.total_forms, icon: LayoutTemplate },
        { label: 'Views', value: overview.total_views, icon: MousePointerSquareDashed },
        { label: 'Starts', value: overview.total_starts, icon: MoveRight },
        { label: 'Submissions', value: overview.total_submissions, icon: PanelsTopLeft },
        { label: 'Avg. conversion', value: `${overview.avg_conversion_rate}%`, icon: ChartColumn },
      ].map((item) => (
        <Card key={item.label} className="overflow-hidden border-slate-200">
          <CardContent className="flex items-center justify-between p-6">
            <div>
              <div className="text-sm text-slate-500">{item.label}</div>
              <div className="mt-2 text-3xl font-black tracking-tight text-slate-900">{item.value}</div>
            </div>
            <div className="rounded-2xl bg-slate-100 p-3 text-slate-700">
              <item.icon className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderLibrary = () => (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-cyan-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-700">
            <LayoutTemplate className="h-3.5 w-3.5" />
            Beluga Forms
          </div>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-900">A cleaner library for form creation and management.</h1>
          <p className="mt-2 max-w-3xl text-slate-500">
            This page is now just the form library. Build and edit inside a dedicated builder screen. View analytics in a separate analytics page.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => navigate('/forms/analytics')}>
            <BarChart3 className="mr-2 h-4 w-4" />
            Open analytics
          </Button>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New form
          </Button>
        </div>
      </div>

      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Your forms</h2>
          <p className="mt-1 text-sm text-slate-500">Choose a form to edit or share. Performance reporting lives on the analytics page.</p>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500">Loading forms...</div>
        ) : forms.length === 0 ? (
          <Alert>
            <AlertDescription>Create your first form to start building reusable iframe experiences.</AlertDescription>
          </Alert>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
            {forms.map((form) => (
              <Card key={form.form_id} className="overflow-hidden border-slate-200 bg-white">
                <CardContent className="space-y-5 p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-lg font-bold text-slate-900">{form.name}</div>
                      <div className="mt-1 text-sm text-slate-500">{form.total_steps} steps</div>
                    </div>
                    <Badge variant="secondary" className="capitalize">{form.status}</Badge>
                  </div>

                  <div className="rounded-2xl bg-slate-50 px-4 py-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Embed ready</div>
                    <div className="mt-2 text-sm leading-6 text-slate-600">
                      Use the builder to shape the experience, then open Share when you are ready to copy the iframe.
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => navigate(`/forms/${form.form_id}`)}>
                      <Edit3 className="mr-2 h-4 w-4" />
                      Edit builder
                    </Button>
                    <Button variant="outline" onClick={() => navigate(`/forms/${form.form_id}/share`)}>
                      <Share2 className="mr-2 h-4 w-4" />
                      Share
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );

  const renderBuilder = () => {
    if (!draftForm) return null;

    const accentColor = draftForm.theme?.accent_color || '#0284c7';
    const panelColor = draftForm.theme?.panel_color || '#ffffff';
    const backgroundColor = draftForm.theme?.background_color || '#f8fbfd';
    const textColor = draftForm.theme?.text_color || '#0f172a';
    const selectedStepIndex = draftForm.steps.findIndex((step) => step.step_id === selectedStep?.step_id);

    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="space-y-3">
            <button type="button" onClick={() => navigate('/forms')} className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-slate-900">
              <ArrowLeft className="h-4 w-4" />
              Back to forms
            </button>
            <div>
              <h1 className="text-3xl font-black tracking-tight text-slate-900">{draftForm.name}</h1>
              <p className="mt-2 max-w-2xl text-slate-500">Builder view only. The share workflow and analytics are now separated so this screen stays focused on editing.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <FormSectionNav formId={draftForm.form_id} isShareRoute={false} isBuilderRoute navigate={navigate} />
            <Button variant="outline" onClick={() => window.open(draftForm.preview_url, '_blank')}>
              <ExternalLink className="mr-2 h-4 w-4" />
              Open preview
            </Button>
            <Button onClick={saveForm} disabled={saving}>
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save form'}
            </Button>
          </div>
        </div>

        <div className="grid gap-6 2xl:grid-cols-[240px_minmax(0,1fr)_340px]">
          <aside className="space-y-4">
            <Card className="overflow-hidden border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fbfd_100%)]">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Sparkles className="h-4 w-4 text-cyan-600" />
                  Elements
                </CardTitle>
                <CardDescription>Drag onto the canvas or click to add to the active page.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 p-3">
                {PALETTE.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.type}
                      type="button"
                      draggable
                      onDragStart={() => setDragItem({ source: 'palette', type: item.type })}
                      onClick={() => selectedStep && addComponentToStep(selectedStep.step_id, item.type)}
                      className="flex w-full items-center gap-3 rounded-[18px] border border-slate-200 bg-white px-3 py-3 text-left transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-sm"
                    >
                      <div className="rounded-2xl bg-slate-100 p-2 text-slate-600">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900">{item.label}</div>
                        <div className="text-xs text-slate-500">{item.description}</div>
                      </div>
                    </button>
                  );
                })}
              </CardContent>
            </Card>

            <Card className="border-slate-200 bg-white">
              <CardContent className="space-y-2 p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <MonitorSmartphone className="h-4 w-4 text-slate-500" />
                  Live preview canvas
                </div>
                <p className="text-sm leading-6 text-slate-500">
                  The center surface now renders the real form style instead of generic nested component boxes.
                </p>
              </CardContent>
            </Card>
          </aside>

          <section className="space-y-5">
            <Card className="overflow-hidden border-slate-200 bg-white">
              <CardHeader className="border-b border-slate-100 bg-[linear-gradient(180deg,#ffffff_0%,#f8fbfd_100%)]">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">Pages</CardTitle>
                    <CardDescription>Move between steps without losing the visual context of the form.</CardDescription>
                  </div>
                  <Button variant="outline" onClick={addStep}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add page
                  </Button>
                </div>
                <div className="mt-4 flex flex-wrap gap-3">
                  {draftForm.steps.map((step, index) => (
                    <div key={step.step_id} className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedStepId(step.step_id);
                          setSelectedComponentId(step.components?.[0]?.component_id || null);
                        }}
                        className={`rounded-[20px] border px-4 py-3 text-left transition ${
                          selectedStepId === step.step_id
                            ? 'border-slate-900 bg-slate-900 text-white shadow-lg'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                      >
                        <div className={`text-[11px] uppercase tracking-[0.18em] ${selectedStepId === step.step_id ? 'text-white/60' : 'text-slate-400'}`}>Step {index + 1}</div>
                        <div className="mt-1 font-semibold">{step.title}</div>
                      </button>
                      {draftForm.steps.length > 1 && (
                        <Button variant="ghost" size="icon" onClick={() => removeStep(step.step_id)}>
                          <Trash2 className="h-4 w-4 text-slate-400" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </CardHeader>
            </Card>

            {selectedStep && (
              <div
                className="rounded-[34px] border border-slate-200 p-5 md:p-8"
                style={{
                  background: `radial-gradient(circle at top, ${accentColor}18, transparent 28%), linear-gradient(180deg, ${backgroundColor} 0%, #eef6ff 100%)`,
                }}
              >
                <div className="mx-auto max-w-[760px]">
                  <div
                    className="overflow-hidden rounded-[34px] border shadow-[0_32px_90px_rgba(15,23,42,0.15)]"
                    style={{ borderColor: `${accentColor}33`, backgroundColor: panelColor }}
                  >
                    <div className="border-b border-slate-100 px-8 py-7" style={{ background: `linear-gradient(135deg, ${accentColor}12, rgba(255,255,255,0.96))` }}>
                      <div className="inline-flex rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.22em]" style={{ backgroundColor: `${accentColor}18`, color: accentColor }}>
                        Step {selectedStepIndex + 1} of {draftForm.steps.length}
                      </div>
                      <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
                        <div>
                          <h2 className="text-[28px] font-black tracking-tight" style={{ color: textColor }}>
                            {selectedStep.title}
                          </h2>
                          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                            {selectedStep.description || 'Use the canvas to shape this step and see the final form styling directly while you edit.'}
                          </p>
                        </div>
                        <div className="min-w-[180px]">
                          <div className="mb-2 flex items-center justify-between text-[11px] uppercase tracking-[0.18em] text-slate-400">
                            <span>Progress</span>
                            <span>{Math.round(((selectedStepIndex + 1) / draftForm.steps.length) * 100)}%</span>
                          </div>
                          <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                            <div
                              className="h-full rounded-full"
                              style={{ width: `${((selectedStepIndex + 1) / draftForm.steps.length) * 100}%`, background: `linear-gradient(90deg, ${accentColor}, #22c55e)` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-5 px-8 py-8">
                      {[...selectedStep.components, { component_id: 'drop-end', type: 'drop-slot' }].map((component, index) => (
                        <div key={`${component.component_id}-${index}`} className="space-y-3">
                          <div
                            onDragOver={(event) => {
                              event.preventDefault();
                              setDragOverIndex(index);
                            }}
                            onDragLeave={() => {
                              if (dragOverIndex === index) setDragOverIndex(null);
                            }}
                            onDrop={() => handleDropAtIndex(selectedStep.step_id, index)}
                            className="relative flex items-center gap-3 py-1"
                          >
                            <div className={`h-px flex-1 transition ${dragOverIndex === index ? 'bg-cyan-400' : 'bg-slate-200'}`} />
                            <div
                              className={`rounded-full border border-dashed px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] transition ${
                                dragOverIndex === index
                                  ? 'border-cyan-400 bg-cyan-50 text-cyan-700 shadow-sm'
                                  : 'border-slate-300 bg-white text-slate-400'
                              }`}
                            >
                              Drop here
                            </div>
                            <div className={`h-px flex-1 transition ${dragOverIndex === index ? 'bg-cyan-400' : 'bg-slate-200'}`} />
                          </div>

                          {component.type !== 'drop-slot' && (
                            <div
                              className="relative"
                              draggable
                              onDragStart={() => beginCanvasDrag(selectedStep.step_id, component.component_id)}
                              onDragEnd={endCanvasDrag}
                            >
                              <div className="absolute -left-4 top-5 z-10">
                                <button
                                  type="button"
                                  onClick={() => setSelectedComponentId(component.component_id)}
                                  className="rounded-2xl border border-slate-200 bg-white p-2 text-slate-500 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
                                >
                                  <GripVertical className="h-4 w-4" />
                                </button>
                              </div>
                              <div className="pl-4">
                                {renderLiveFormComponent({
                                  component,
                                  selected: selectedComponentId === component.component_id,
                                  accentColor,
                                  onSelect: () => setSelectedComponentId(component.component_id),
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-8 py-6">
                      <Button variant="outline" className="rounded-2xl px-5">
                        {selectedStep.back_button_text || 'Back'}
                      </Button>
                      <Button className="rounded-2xl px-6 text-white" style={{ background: `linear-gradient(135deg, ${accentColor}, #0369a1)` }}>
                        {selectedStepIndex < draftForm.steps.length - 1 ? selectedStep.next_button_text || 'Continue' : selectedStep.submit_button_text || 'Submit'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          <Card className="border-slate-200 bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Settings2 className="h-4 w-4 text-cyan-600" />
                Inspector
              </CardTitle>
              <CardDescription>Contextual controls for the current form, step, and selected element.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label>Form name</Label>
                <Input value={draftForm.name} onChange={(event) => updateDraft((next) => ({ ...next, name: event.target.value }))} />
              </div>

              <div className="space-y-3">
                <Label>Campaign</Label>
                <Select value={draftForm.campaign_id} onValueChange={(value) => updateDraft((next) => ({ ...next, campaign_id: value }))}>
                  <SelectTrigger><SelectValue placeholder="Select campaign" /></SelectTrigger>
                  <SelectContent>
                    {campaigns.map((campaign) => (
                      <SelectItem key={campaign.campaign_id} value={campaign.campaign_id}>{campaign.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label>Allowed domains</Label>
                <Textarea
                  value={draftForm.allowed_domains.join(', ')}
                  onChange={(event) => updateDraft((next) => ({
                    ...next,
                    allowed_domains: event.target.value.split(/[\n,]/).map((item) => item.trim()).filter(Boolean),
                  }))}
                  placeholder="example.com, landing.example.com"
                />
              </div>

              <div className="space-y-3">
                <Label>Success message</Label>
                <Textarea value={draftForm.success_message} onChange={(event) => updateDraft((next) => ({ ...next, success_message: event.target.value }))} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Accent</Label>
                  <Input type="color" value={draftForm.theme.accent_color} onChange={(event) => updateDraft((next) => ({ ...next, theme: { ...next.theme, accent_color: event.target.value } }))} />
                </div>
                <div className="space-y-2">
                  <Label>Panel</Label>
                  <Input type="color" value={draftForm.theme.panel_color} onChange={(event) => updateDraft((next) => ({ ...next, theme: { ...next.theme, panel_color: event.target.value } }))} />
                </div>
                <div className="space-y-2">
                  <Label>Background</Label>
                  <Input type="color" value={draftForm.theme.background_color} onChange={(event) => updateDraft((next) => ({ ...next, theme: { ...next.theme, background_color: event.target.value } }))} />
                </div>
                <div className="space-y-2">
                  <Label>Text</Label>
                  <Input type="color" value={draftForm.theme.text_color} onChange={(event) => updateDraft((next) => ({ ...next, theme: { ...next.theme, text_color: event.target.value } }))} />
                </div>
              </div>

              {selectedStep && (
                <div className="space-y-3 rounded-[26px] border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">Page settings</div>
                  <Input value={selectedStep.title} onChange={(event) => updateStepField(selectedStep.step_id, 'title', event.target.value)} placeholder="Page title" />
                  <Textarea value={selectedStep.description || ''} onChange={(event) => updateStepField(selectedStep.step_id, 'description', event.target.value)} placeholder="Page description" />
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Input value={selectedStep.next_button_text} onChange={(event) => updateStepField(selectedStep.step_id, 'next_button_text', event.target.value)} placeholder="Next button" />
                    <Input value={selectedStep.back_button_text} onChange={(event) => updateStepField(selectedStep.step_id, 'back_button_text', event.target.value)} placeholder="Back button" />
                  </div>
                  <Input value={selectedStep.submit_button_text} onChange={(event) => updateStepField(selectedStep.step_id, 'submit_button_text', event.target.value)} placeholder="Submit button" />
                </div>
              )}

              {selectedComponent && (
                <div className="space-y-4 rounded-[26px] border border-cyan-200 bg-cyan-50/60 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-[0.18em] text-cyan-700">Selected element</div>
                      <div className="mt-1 font-semibold text-slate-900">{selectedComponent.label || selectedComponent.text || selectedComponent.type}</div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeComponent(selectedComponent.component_id)}>
                      <Trash2 className="h-4 w-4 text-slate-500" />
                    </Button>
                  </div>

                  {(selectedComponent.type === 'heading' || selectedComponent.type === 'paragraph') && (
                    <Textarea value={selectedComponent.text || ''} onChange={(event) => updateComponent(selectedComponent.component_id, 'text', event.target.value)} placeholder="Component text" />
                  )}

                  {!['heading', 'paragraph'].includes(selectedComponent.type) && (
                    <>
                      <div className="space-y-2">
                        <Label>Label</Label>
                        <Input value={selectedComponent.label || ''} onChange={(event) => updateComponent(selectedComponent.component_id, 'label', event.target.value)} />
                      </div>

                      {!['checkbox', 'radio_group', 'checkbox_group'].includes(selectedComponent.type) && (
                        <div className="space-y-2">
                          <Label>Placeholder</Label>
                          <Input value={selectedComponent.placeholder || ''} onChange={(event) => updateComponent(selectedComponent.component_id, 'placeholder', event.target.value)} />
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label>Field key</Label>
                        <Input value={selectedComponent.field_key || ''} onChange={(event) => updateComponent(selectedComponent.component_id, 'field_key', slugify(event.target.value, 'field'))} />
                      </div>

                      <div className="space-y-2">
                        <Label>Maps to CRM field</Label>
                        <Select value={selectedComponent.lead_field || 'custom'} onValueChange={(value) => updateComponent(selectedComponent.component_id, 'lead_field', value)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="custom">Custom only</SelectItem>
                            <SelectItem value="name">Lead name</SelectItem>
                            <SelectItem value="email">Lead email</SelectItem>
                            <SelectItem value="phone">Lead phone</SelectItem>
                            <SelectItem value="message">Lead notes</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-3 py-3">
                        <div>
                          <div className="font-medium text-slate-900">Required</div>
                          <div className="text-xs text-slate-500">Visitors must complete this before moving on.</div>
                        </div>
                        <Switch checked={selectedComponent.required} onCheckedChange={(checked) => updateComponent(selectedComponent.component_id, 'required', checked)} />
                      </div>

                      <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-3 py-3">
                        <div>
                          <div className="font-medium text-slate-900">Enabled</div>
                          <div className="text-xs text-slate-500">Disable without deleting the block.</div>
                        </div>
                        <Switch checked={selectedComponent.enabled} onCheckedChange={(checked) => updateComponent(selectedComponent.component_id, 'enabled', checked)} />
                      </div>
                    </>
                  )}

                  {['select', 'radio_group', 'checkbox_group'].includes(selectedComponent.type) && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-slate-900">Options</div>
                        <Button variant="outline" size="sm" onClick={addOptionToComponent}>
                          <Plus className="mr-2 h-3.5 w-3.5" />
                          Add option
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {selectedComponent.options.map((option) => (
                          <div key={option.option_id} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                            <Input value={option.label} onChange={(event) => updateOption(selectedComponent.component_id, option.option_id, 'label', event.target.value)} placeholder="Label" />
                            <Input value={option.value} onChange={(event) => updateOption(selectedComponent.component_id, option.option_id, 'value', event.target.value)} placeholder="Value" />
                            <Button variant="ghost" size="icon" onClick={() => removeOption(selectedComponent.component_id, option.option_id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const renderShare = () => {
    if (!draftForm) return null;
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="space-y-3">
            <button type="button" onClick={() => navigate('/forms')} className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-slate-900">
              <ArrowLeft className="h-4 w-4" />
              Back to forms
            </button>
            <div>
              <h1 className="text-3xl font-black tracking-tight text-slate-900">{draftForm.name}</h1>
              <p className="mt-2 max-w-2xl text-slate-500">Sharing has its own page now. Embed instructions, preview, and domain notes are no longer mixed into the builder.</p>
            </div>
          </div>
          <FormSectionNav formId={draftForm.form_id} isShareRoute isBuilderRoute={false} navigate={navigate} />
        </div>

        <div className="grid gap-6 xl:grid-cols-[420px_minmax(0,1fr)]">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Embed</CardTitle>
              <CardDescription>Use the iframe code on any allowed host site.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label>Embed URL</Label>
                <div className="flex gap-2">
                  <Input value={draftForm.embed_url} readOnly />
                  <Button variant="outline" onClick={() => copyToClipboard(draftForm.embed_url, 'Embed URL')}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Iframe code</Label>
                <Textarea value={draftForm.iframe_code} readOnly className="min-h-[190px] font-mono text-xs" />
              </div>

              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => copyToClipboard(draftForm.iframe_code, 'Iframe code')}>
                  <Copy className="mr-2 h-4 w-4" />
                  Copy iframe
                </Button>
                <Button variant="outline" onClick={() => window.open(draftForm.preview_url, '_blank')}>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Open preview
                </Button>
              </div>

              <Alert>
                <Globe className="h-4 w-4" />
                <AlertDescription>
                  Save changes before embedding them. Only hosts listed in allowed domains can load the form outside preview mode.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Live preview</CardTitle>
              <CardDescription>This uses the actual embed route, not a fake React-only preview.</CardDescription>
            </CardHeader>
            <CardContent>
              <iframe
                title={`${draftForm.name} preview`}
                src={draftForm.preview_url}
                className="h-[820px] w-full rounded-[30px] border border-slate-200 bg-white"
              />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  const renderAnalytics = () => (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">
            <BarChart3 className="h-3.5 w-3.5" />
            Forms Analytics
          </div>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-900">Analytics has its own page now.</h1>
          <p className="mt-2 max-w-3xl text-slate-500">
            Filter across all forms or switch to a specific form. This screen is now separate from the builder so performance review stays readable.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => navigate('/forms')}>
            <LayoutTemplate className="mr-2 h-4 w-4" />
            Form library
          </Button>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New form
          </Button>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="grid gap-4 p-6 lg:grid-cols-[260px_260px_260px_minmax(0,1fr)] lg:items-end">
          <div className="grid gap-2">
            <Label>Scope</Label>
            <Select value={analyticsFilter} onValueChange={setAnalyticsFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All forms</SelectItem>
                {forms.map((form) => (
                  <SelectItem key={form.form_id} value={form.form_id}>{form.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>From</Label>
            <Input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label>To</Label>
            <Input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </div>
          {selectedAnalyticsForm && (
            <div className="flex flex-wrap gap-2 lg:justify-end">
              <Button variant="outline" onClick={() => navigate(`/forms/${selectedAnalyticsForm.form_id}`)}>
                <Edit3 className="mr-2 h-4 w-4" />
                Open builder
              </Button>
              <Button variant="outline" onClick={() => navigate(`/forms/${selectedAnalyticsForm.form_id}/share`)}>
                <Share2 className="mr-2 h-4 w-4" />
                Open share page
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {analyticsSummary && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          {[
            { label: 'Views', value: analyticsSummary.total_views },
            { label: 'Starts', value: analyticsSummary.total_starts },
            { label: 'Submissions', value: analyticsSummary.total_submissions },
            { label: 'View conversion', value: `${analyticsSummary.conversion_rate}%` },
            { label: 'Start conversion', value: `${analyticsSummary.start_to_submission_rate}%` },
          ].map((metric) => (
            <Card key={metric.label} className="border-slate-200">
              <CardContent className="p-6">
                <div className="text-sm text-slate-500">{metric.label}</div>
                <div className="mt-2 text-3xl font-black tracking-tight text-slate-900">{metric.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {analyticsSummary && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle>{analyticsFilter === 'all' ? 'Time series across all forms' : 'Time series for selected form'}</CardTitle>
            <CardDescription>Views, starts, and submissions over the selected date range.</CardDescription>
          </CardHeader>
          <CardContent className="h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={analyticsSummary.time_series}>
                <defs>
                  <linearGradient id="viewsFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.22} />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="submissionsFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.18} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 12 }} />
                <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="views" stroke="#0891b2" fill="url(#viewsFill)" strokeWidth={2} />
                <Area type="monotone" dataKey="starts" stroke="#6366f1" fillOpacity={0} strokeWidth={2} />
                <Area type="monotone" dataKey="submissions" stroke="#16a34a" fill="url(#submissionsFill)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {analyticsFilter === 'all' ? (
        <>
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>All forms</CardTitle>
              <CardDescription>Quick comparison across the current business.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {(analyticsSummary?.form_breakdown || []).map((form) => {
                const conversion = Number(form.conversion_rate || 0).toFixed(1);
                return (
                  <div key={form.form_id} className="grid grid-cols-[minmax(0,1.5fr)_100px_100px_120px_auto] items-center gap-4 rounded-3xl border border-slate-200 px-4 py-4">
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-slate-900">{form.name}</div>
                      <div className="text-sm text-slate-500">Beluga form</div>
                    </div>
                    <div className="text-sm text-slate-600">
                      <div className="text-xs uppercase tracking-[0.16em] text-slate-400">Views</div>
                      <div className="mt-1 font-semibold text-slate-900">{form.views}</div>
                    </div>
                    <div className="text-sm text-slate-600">
                      <div className="text-xs uppercase tracking-[0.16em] text-slate-400">Submits</div>
                      <div className="mt-1 font-semibold text-slate-900">{form.submissions}</div>
                    </div>
                    <div className="text-sm text-slate-600">
                      <div className="text-xs uppercase tracking-[0.16em] text-slate-400">Conversion</div>
                      <div className="mt-1 font-semibold text-slate-900">{conversion}%</div>
                    </div>
                    <div className="flex justify-end">
                      <Button variant="outline" onClick={() => setAnalyticsFilter(form.form_id)}>
                        Drill in
                      </Button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </>
      ) : analytics ? (
        <>
          <div className="grid gap-6 xl:grid-cols-2">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Step funnel</CardTitle>
                <CardDescription>Progression and dropoff per page.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {analytics.step_stats.map((step, index) => (
                  <div key={step.step_id} className="rounded-3xl border border-slate-200 p-5">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="text-xs uppercase tracking-[0.16em] text-slate-400">Step {index + 1}</div>
                        <div className="mt-1 text-lg font-semibold text-slate-900">{step.title}</div>
                      </div>
                      <Badge variant="secondary">{step.completion_rate}% completion</Badge>
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                      <div className="rounded-2xl bg-slate-50 px-3 py-3">
                        <div className="text-slate-500">Views</div>
                        <div className="mt-1 font-semibold text-slate-900">{step.views}</div>
                      </div>
                      <div className="rounded-2xl bg-slate-50 px-3 py-3">
                        <div className="text-slate-500">Completions</div>
                        <div className="mt-1 font-semibold text-slate-900">{step.completions}</div>
                      </div>
                      <div className="rounded-2xl bg-slate-50 px-3 py-3">
                        <div className="text-slate-500">Dropoff</div>
                        <div className="mt-1 font-semibold text-slate-900">{step.dropoff_rate}%</div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Lead outcomes</CardTitle>
                <CardDescription>How this form performs after the submit.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {Object.entries(analytics.stage_breakdown).map(([stage, count]) => (
                  <div key={stage} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="capitalize text-slate-600">{stage}</span>
                      <span className="font-semibold text-slate-900">{count}</span>
                    </div>
                    <div className="h-3 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-600"
                        style={{ width: `${Math.min(100, ((count || 0) / Math.max(1, analytics.submissions)) * 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
                {isSuperAdmin && (
                  <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-4">
                    <div className="text-sm text-emerald-700">Won revenue</div>
                    <div className="mt-2 text-2xl font-bold text-emerald-900">£{analytics.won_revenue}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 xl:grid-cols-3">
            <Card className="border-slate-200">
              <CardHeader><CardTitle>Top domains</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(analytics.top_domains).map(([domain, count]) => (
                  <div key={domain} className="flex items-center justify-between text-sm">
                    <span className="truncate text-slate-600">{domain}</span>
                    <span className="font-semibold text-slate-900">{count}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader><CardTitle>Top pages</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(analytics.top_pages).map(([page, count]) => (
                  <div key={page} className="flex items-center justify-between gap-3 text-sm">
                    <span className="truncate text-slate-600">{page}</span>
                    <span className="font-semibold text-slate-900">{count}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader><CardTitle>Top sources</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(analytics.top_utm_sources).map(([source, count]) => (
                  <div key={source} className="flex items-center justify-between text-sm">
                    <span className="truncate text-slate-600">{source}</span>
                    <span className="font-semibold text-slate-900">{count}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {analyticsSummary && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Daily submissions</CardTitle>
                <CardDescription>A simpler daily bar view for the selected form.</CardDescription>
              </CardHeader>
              <CardContent className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analyticsSummary.time_series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 12 }} />
                    <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="submissions" fill="#0ea5e9" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <div className="text-sm text-slate-500">Select a form to load its analytics.</div>
      )}
    </div>
  );

  if (user?.role !== 'super_admin' && user?.role !== 'admin') {
    return <div className="text-slate-500">Admin access required.</div>;
  }

  if (!businessId) {
    return (
      <Alert>
        <AlertDescription>Select a business before creating or editing forms.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {isAnalyticsRoute ? renderAnalytics() : isShareRoute ? renderShare() : isBuilderRoute ? renderBuilder() : renderLibrary()}

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Create embedded form</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Form name</Label>
              <Input value={createData.name} onChange={(event) => setCreateData((prev) => ({ ...prev, name: event.target.value }))} placeholder="Admissions enquiry form" />
            </div>
            <div className="space-y-2">
              <Label>Campaign</Label>
              <Select value={createData.campaign_id} onValueChange={(value) => setCreateData((prev) => ({ ...prev, campaign_id: value }))}>
                <SelectTrigger><SelectValue placeholder="Select campaign" /></SelectTrigger>
                <SelectContent>
                  {campaigns.map((campaign) => (
                    <SelectItem key={campaign.campaign_id} value={campaign.campaign_id}>{campaign.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Alert>
              <AlertDescription>
                New forms start as a two-step builder template. Builder, sharing, and analytics are now separated after creation.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={createForm}>Create form</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FormsPage;
